/** Fetch wrapper: same-origin in dev (Vite proxies /api), VITE_API_URL in production. Carries the session cookie and CSRF token. */
export const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
let csrfToken = '';
export const setCsrf = (t: string | null | undefined) => { csrfToken = t ?? ''; };

/** Called when a request comes back 401 (the store signs out and shows "session expired"). Auth endpoints are exempt: a bad password is a 401 too. */
let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (fn: (() => void) | null) => { onUnauthorized = fn; };

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) { super(message); }
}

export async function api<T = unknown>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown, init: RequestInit = {}): Promise<T> {
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const res = await fetch(API_BASE + path, {
    method, credentials: 'include',
    headers: { ...(isForm ? {} : { 'Content-Type': 'application/json' }), ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}), ...(init.headers ?? {}) },
    body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    ...init,
  });
  const json = (await res.json().catch(() => ({ error: 'Bad response from the server' }))) as T & { error?: string; code?: string; csrf?: string };
  if (res.status === 401 && !path.startsWith('/api/auth/')) onUnauthorized?.();
  if (res.status === 429 && (!json.error || json.error === 'Too Many Requests')) throw new ApiError('Too many attempts — wait a minute and try again.', 429, json.code);
  if (!res.ok) throw new ApiError(json.error ?? `Request failed (${res.status})`, res.status, json.code);
  if (json.csrf) setCsrf(json.csrf);
  return json;
}
export const get = <T = unknown>(p: string) => api<T>('GET', p);
export const post = <T = unknown>(p: string, b: unknown = {}) => api<T>('POST', p, b);
export const put = <T = unknown>(p: string, b: unknown = {}) => api<T>('PUT', p, b);
export const del = <T = unknown>(p: string, b: unknown = {}) => api<T>('DELETE', p, b);

export const wsUrl = () => (API_BASE || location.origin).replace(/^http/, 'ws') + '/ws';
/** Content hashes of public/cards/*.webp, baked in at build time, so a rescan gets a new URL past every cache. */
declare const __ART_V__: Record<string, string> | undefined;
const ART_V: Record<string, string> = typeof __ART_V__ === 'undefined' ? {} : __ART_V__;
export const versionArt = (art: string, versions: Record<string, string> = ART_V) => { const m = /^\/cards\/([^/?#]+)$/.exec(art); const v = m && versions[m[1]]; return v ? `${art}?v=${v}` : art; };
/** Card art: Ben's scans ship with the web app (`/cards/x.webp`, versioned); admin-added art comes from the API. */
export const artUrl = (art: string | null | undefined) => (!art ? null : art.startsWith('http') ? art : art.startsWith('/files/') ? API_BASE + art : versionArt(art));
export const fileUrl = (path: string) => API_BASE + path;
